import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/utils/api";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { Noise } from "@/components/Noise";
import { BottomNav } from "@/components/BottomNav";
import LandingPage from "@/pages/LandingPage";
import AppPreviewPage from "@/pages/AppPreviewPage";
import AdminPage from "@/pages/AdminPage";
import AccountPage from "@/pages/AccountPage";
import AccountPlansPage from "@/pages/AccountPlansPage";
import LegalPage from "@/pages/LegalPage";
import WardrobePage from "@/pages/WardrobePage";
import { initTelegramFullscreen, subscribeTopInset } from "@/utils/telegram";
import { setUserToken } from "@/utils/auth";
import { getClientKey, getPhone } from "@/utils/access";
import { saveSession, getEntrySnapshot, clearSession } from "@/utils/session";

// Запоминаем последний открытый раздел — Mini App вернётся в него при повторном запуске
const RouteMemory = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    saveSession({ route: pathname });
  }, [pathname]);
  return null;
};

// Возвращение к последнему месту: при открытии Mini App со свежей сессией (5 минут)
// СПРАШИВАЕМ, продолжить ли с того же места — вместо молчаливого прыжка. Один раз за запуск.
const ResumePrompt = () => {
  const [entry, setEntry] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    if (window.__dpResumeDecided) return;
    window.__dpResumeDecided = true; // один раз за открытие приложения
    const tg = window.Telegram?.WebApp;
    if (!tg?.initData) return; // вопрос только в мини-приложении; браузер не трогаем
    const snap = getEntrySnapshot();
    if (!snap) return;
    const route = snap.route || (snap.photo ? "/preview" : "");
    if (!["/preview", "/account", "/account/tariffs"].includes(route)) return;
    setEntry({ ...snap, route });
  }, []);
  if (!entry) return null;
  return (
    <div
      className="fixed inset-0 z-[200] flex items-end justify-center bg-[#0A0A0A]/85 px-6 pb-16"
      data-testid="resume-prompt"
    >
      <div className="w-full max-w-sm rounded-3xl border border-white/10 bg-[#121210] p-7">
        <p className="font-jetbrains text-[9px] uppercase tracking-[0.3em] text-[#C2A878]">
          ( Возвращение )
        </p>
        <h2 className="mt-3 font-cormorant text-3xl font-light leading-tight text-[#F4F4F0]">
          Продолжить с того же места?
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-[#A3A3A3]">
          Ваше фото и разбор сохранились. Храним их 5 минут после закрытия.
        </p>
        <div className="mt-7 flex flex-col gap-3">
          <button
            onClick={() => {
              setEntry(null);
              navigate(entry.route);
            }}
            className="w-full border border-[#C2A878] py-3.5 font-jetbrains text-[11px] uppercase tracking-[0.25em] text-[#C2A878] transition-colors duration-500 hover:bg-[#C2A878] hover:text-[#0A0A0A]"
            data-testid="resume-continue-button"
          >
            Продолжить
          </button>
          <button
            onClick={() => {
              clearSession();
              setEntry(null);
            }}
            className="w-full py-3 font-jetbrains text-[11px] uppercase tracking-[0.25em] text-[#525252] transition-colors duration-500 hover:text-[#A3A3A3]"
            data-testid="resume-restart-button"
          >
            Начать заново
          </button>
        </div>
      </div>
    </div>
  );
};

// Сплошная плашка в зоне верхнего отступа Telegram fullscreen,
// чтобы скроллящийся контент не просвечивал под системными кнопками Telegram
const TopInsetBar = () => {
  const [topInset, setTopInset] = useState(0);
  useEffect(() => subscribeTopInset(setTopInset), []);
  if (!topInset) return null;
  return (
    <div
      className="fixed inset-x-0 top-0 z-50 bg-[#0A0A0A]"
      style={{ height: topInset }}
      data-testid="tg-top-inset-bar"
    />
  );
};

// Нижняя навигация: везде, кроме полноэкранного редактора и админки
const BottomNavGate = () => {
  const { pathname } = useLocation();
  if (pathname === "/preview" || pathname === "/admin") return null;
  return <BottomNav />;
};

function App() {
  useEffect(() => {
    const tg = window.Telegram?.WebApp;
    if (tg) {
      try {
        tg.setHeaderColor("#0A0A0A");
        tg.setBackgroundColor("#0A0A0A");
      } catch {}
    }
    initTelegramFullscreen();

    // Тихий вход в Telegram Mini App: аккаунт создаётся по подписанному initData.
    // Токен обновляем при каждом открытии — так не протухает через 30 дней.
    if (tg?.initData) {
      axios
        .post(`${API}/auth/telegram/miniapp`, {
          initData: tg.initData,
          client_key: getClientKey(),
          phone: getPhone(),
        })
        .then(({ data }) => setUserToken(data.token))
        .catch(() => {});
    }

    const lenis = new Lenis({ lerp: 0.09, anchors: true });
    window.__lenis = lenis;
    let rafId;
    const loop = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(loop);
    };
    rafId = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <BrowserRouter>
      <Noise />
      <TopInsetBar />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/preview" element={<AppPreviewPage />} />
        <Route path="/wardrobe" element={<WardrobePage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/account" element={<AccountPage />} />
        <Route path="/account/tariffs" element={<AccountPlansPage />} />
        <Route path="/privacy" element={<LegalPage doc="privacy" />} />
        <Route path="/offer" element={<LegalPage doc="offer" />} />
        <Route path="/consent" element={<LegalPage doc="consent" />} />
        <Route path="/requisites" element={<LegalPage doc="requisites" />} />
      </Routes>
      <BottomNavGate />
      <RouteMemory />
      <ResumePrompt />
      <Toaster theme="dark" position="bottom-right" />
    </BrowserRouter>
  );
}

export default App;
